import useBreadcrumb from '../../Hooks/useBreadcrumb';
import './PrivacyPolicyPage.css';

export default function PrivacyPolicyPage () {
    const BreadCrumb = useBreadcrumb('Privacy Policy');
    return (
        <div style={{ minHeight: '100vh' }}>
            {BreadCrumb}

            <section className='inner-page'>
                <div className='container'>
                    <div className='privacy-policy-content'>
                        <h1>Privacy Policy</h1>
                        <p className='last-updated'>Last updated: {new Date().toLocaleDateString()}</p>

                        <section>
                            <h2>1. Introduction</h2>
                            <p>
                                Welcome to HydroMastery. We are committed to protecting your personal information and
                                your right to privacy. This Privacy Policy explains how we collect, use, disclose, and
                                safeguard your information when you use our web application.
                            </p>
                            <p>
                                <strong>Note on Service Fees:</strong> HydroMastery is currently provided free of charge.
                                However, we reserve the right to introduce fees, subscriptions, or charges for the service
                                or certain features in the future. If fees are implemented, we will provide advance notice
                                and clear disclosure of any pricing changes. Your privacy rights and data protection
                                practices will remain the same regardless of whether the service is free or paid.
                            </p>
                        </section>

                        <section>
                            <h2>2. Information We Collect</h2>
                            <h3>2.1 Personal Information</h3>
                            <p>We may collect personal information that you provide to us, including:</p>
                            <ul>
                                <li>Name and contact information (email address)</li>
                                <li>Account credentials (password, which is securely hashed)</li>
                                <li>Profile information (first name, last name, user type)</li>
                                <li>Athletic performance data (workouts, tests, race results)</li>
                                <li>Training program information</li>
                            </ul>

                            <h3>2.2 Automatically Collected Information</h3>
                            <p>We may automatically collect certain information when you use our service:</p>
                            <ul>
                                <li>Device information and identifiers</li>
                                <li>Usage data and analytics</li>
                                <li>Cookies and similar tracking technologies</li>
                            </ul>
                        </section>

                        <section>
                            <h2>3. How We Use Your Information</h2>
                            <p>We use the information we collect to:</p>
                            <ul>
                                <li>Provide, maintain, and improve our services</li>
                                <li>Process your account registration and manage your account</li>
                                <li>Enable communication between coaches and athletes</li>
                                <li>Analyze and track your athletic performance</li>
                                <li>Send you important service-related updates and notifications</li>
                                <li>Send you transactional emails (account verification, password resets, etc.)</li>
                                <li>Send you marketing communications only with your explicit consent</li>
                                <li>Respond to your inquiries and provide customer support</li>
                                <li>Detect and prevent fraud or abuse</li>
                                <li>Comply with legal obligations and enforce our terms</li>
                            </ul>
                        </section>

                        <section>
                            <h2>3.1 Email Communications</h2>
                            <p>
                                We use email to communicate with you for service-related purposes. We comply with the CAN-SPAM Act, GDPR, and other applicable email regulations.
                            </p>
                            <h3>3.1.1 Transactional Emails</h3>
                            <p>
                                We send transactional emails that are necessary for the operation of our service, including:
                            </p>
                            <ul>
                                <li>Account verification and activation emails</li>
                                <li>Password reset and security notifications</li>
                                <li>Service updates and important announcements</li>
                                <li>Relationship requests and approvals between coaches and athletes</li>
                                <li>Workout and training program notifications</li>
                            </ul>
                            <p>These emails are sent to all users as they are essential for service functionality.</p>

                            <h3>3.1.2 Marketing Communications</h3>
                            <p>
                                We only send marketing emails (newsletters, promotional content, feature announcements) to users who have explicitly opted in. You can opt out at any time by:
                            </p>
                            <ul>
                                <li>Clicking the unsubscribe link in any marketing email</li>
                                <li>Updating your email preferences in your account settings</li>
                                <li>Contacting us directly at <a href='mailto:hydromastery@gmail.com'>hydromastery@gmail.com</a></li>
                            </ul>
                            <p>
                                We honor opt-out requests within 10 business days. Note that opting out of marketing emails will not affect transactional emails necessary for service operation.
                            </p>

                            <h3>3.1.3 Email Service Provider</h3>
                            <p>
                                We use Amazon Web Services (AWS) Simple Email Service (SES) to send emails. AWS SES processes emails on our behalf in accordance with their data processing agreements and security standards. All emails are transmitted using industry-standard encryption protocols.
                            </p>
                        </section>

                        <section>
                            <h2>4. Information Sharing and Disclosure</h2>
                            <p>We do not sell, rent, or trade your personal information. We may share your information only in the following circumstances:</p>
                            <ul>
                                <li>
                                    <strong>With Your Consent:</strong> We may share information when you explicitly
                                    consent to such sharing
                                </li>
                                <li>
                                    <strong>Service Providers:</strong> We may share information with third-party
                                    service providers who perform services on our behalf, including:
                                    <ul>
                                        <li>Amazon Web Services (AWS) for cloud hosting and email services (SES)</li>
                                        <li>MongoDB Atlas for database services</li>
                                        <li>Other service providers bound by confidentiality agreements</li>
                                    </ul>
                                    All service providers are contractually obligated to protect your data and use it only for specified purposes.
                                </li>
                                <li>
                                    <strong>Legal Requirements:</strong> We may disclose information if required by law,
                                    court order, or in response to valid requests by public authorities, including to:
                                    <ul>
                                        <li>Comply with legal obligations</li>
                                        <li>Respond to government requests</li>
                                        <li>Enforce our terms and policies</li>
                                        <li>Protect our rights, privacy, safety, or property</li>
                                    </ul>
                                </li>
                                <li>
                                    <strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale
                                    of assets, your information may be transferred, subject to the same privacy protections
                                </li>
                                <li>
                                    <strong>With Other Users:</strong> As part of our service functionality, coaches and athletes may share performance data and training information with each other when they establish relationships through our platform
                                </li>
                            </ul>
                        </section>

                        <section>
                            <h2>5. Data Security</h2>
                            <p>
                                We implement comprehensive technical and organizational security measures to protect your
                                personal information, including:
                            </p>
                            <ul>
                                <li>
                                    <strong>Encryption:</strong> All data in transit is encrypted using TLS/SSL protocols.
                                    Sensitive data at rest is encrypted using industry-standard encryption algorithms.
                                </li>
                                <li>
                                    <strong>Access Controls:</strong> We implement role-based access controls and
                                    authentication mechanisms. Access to personal data is restricted to authorized
                                    personnel only and is logged and monitored.
                                </li>
                                <li>
                                    <strong>Password Security:</strong> Passwords are hashed using bcrypt with salt,
                                    ensuring they cannot be recovered in plain text.
                                </li>
                                <li>
                                    <strong>Secure Infrastructure:</strong> Our services are hosted on AWS infrastructure
                                    with security best practices, including network isolation, firewalls, and intrusion
                                    detection systems.
                                </li>
                                <li>
                                    <strong>Regular Security Audits:</strong> We conduct regular security assessments,
                                    vulnerability scans, and penetration testing to identify and address security issues.
                                </li>
                                <li>
                                    <strong>Incident Response:</strong> We maintain an incident response plan to quickly
                                    address and mitigate security breaches.
                                </li>
                                <li>
                                    <strong>Data Backup:</strong> Regular automated backups are performed to ensure data
                                    availability and recovery capabilities.
                                </li>
                            </ul>
                            <p>
                                While we implement industry-standard security measures, no method of transmission over the
                                Internet or electronic storage is 100% secure. We cannot guarantee absolute security, but
                                we are committed to protecting your data and will notify you promptly in the event of a
                                security breach that may affect your personal information.
                            </p>
                        </section>

                        <section>
                            <h2>5.1 Security Incident Response</h2>
                            <p>
                                In the event of a security incident or data breach that may affect your personal information,
                                we will:
                            </p>
                            <ul>
                                <li>Investigate the incident immediately and take steps to contain and remediate it</li>
                                <li>Notify affected users within 72 hours of becoming aware of the breach, where required by law</li>
                                <li>Report the incident to relevant data protection authorities as required by applicable regulations</li>
                                <li>Provide information about the nature of the breach and steps we are taking to address it</li>
                                <li>Take measures to prevent similar incidents in the future</li>
                            </ul>
                        </section>

                        <section>
                            <h2>6. Your Rights (GDPR and Data Protection Rights)</h2>
                            <p>
                                Depending on your location, you may have the following rights regarding your personal data:
                            </p>
                            <ul>
                                <li>
                                    <strong>Right of Access:</strong> You have the right to access and receive a copy of
                                    your personal data that we hold, including information about how we process it.
                                </li>
                                <li>
                                    <strong>Right to Rectification:</strong> You can request correction of inaccurate or
                                    incomplete personal data.
                                </li>
                                <li>
                                    <strong>Right to Erasure ("Right to be Forgotten"):</strong> You can request deletion
                                    of your personal data when it is no longer necessary for the purposes for which it
                                    was collected, or when you withdraw consent.
                                </li>
                                <li>
                                    <strong>Right to Object:</strong> You can object to processing of your personal data
                                    for certain purposes, including direct marketing.
                                </li>
                                <li>
                                    <strong>Right to Restrict Processing:</strong> You can request that we limit how we
                                    process your personal data in certain circumstances.
                                </li>
                                <li>
                                    <strong>Right to Data Portability:</strong> You can request that we provide your
                                    personal data in a structured, commonly used, and machine-readable format.
                                </li>
                                <li>
                                    <strong>Right to Withdraw Consent:</strong> Where processing is based on consent, you
                                    can withdraw consent at any time.
                                </li>
                                <li>
                                    <strong>Right to Lodge a Complaint:</strong> You have the right to file a complaint
                                    with your local data protection authority if you believe we have violated your data
                                    protection rights.
                                </li>
                            </ul>
                            <p>
                                To exercise any of these rights, please contact us at{' '}
                                <a href='mailto:hydromastery@gmail.com'>hydromastery@gmail.com</a>. We will respond to
                                your request within 30 days. We may need to verify your identity before processing your request.
                            </p>
                        </section>

                        <section>
                            <h2>7. Data Retention and Deletion</h2>
                            <p>
                                We retain your personal information only for as long as necessary to fulfill the purposes
                                outlined in this Privacy Policy, unless a longer retention period is required or
                                permitted by law. Our retention practices include:
                            </p>
                            <ul>
                                <li>
                                    <strong>Active Accounts:</strong> We retain data for active accounts for the duration
                                    of your use of the service.
                                </li>
                                <li>
                                    <strong>Deleted Accounts:</strong> When you delete your account, we will delete or
                                    anonymize your personal data within 30 days, except where we are required to retain it
                                    for legal, regulatory, or legitimate business purposes.
                                </li>
                                <li>
                                    <strong>Legal Requirements:</strong> We may retain certain data longer if required by
                                    law, court order, or to resolve disputes.
                                </li>
                                <li>
                                    <strong>Backup Data:</strong> Data in backups may be retained for up to 90 days before
                                    permanent deletion.
                                </li>
                                <li>
                                    <strong>Email Records:</strong> Email logs and metadata may be retained for up to 12
                                    months for security and compliance purposes.
                                </li>
                            </ul>
                            <p>
                                You can request deletion of your data at any time by contacting us. We will process
                                deletion requests in accordance with applicable law and this policy.
                            </p>
                        </section>

                        <section>
                            <h2>8. Children's Privacy (COPPA Compliance)</h2>
                            <p>
                                Our service is not intended for children under the age of 13 (or 16 in the EU). We do not
                                knowingly collect personal information from children under the applicable age threshold.
                                If you are a parent or guardian and believe your child has provided us with personal
                                information, please contact us immediately at{' '}
                                <a href='mailto:hydromastery@gmail.com'>hydromastery@gmail.com</a>. We will promptly
                                delete such information upon verification.
                            </p>
                            <p>
                                If we become aware that we have collected personal information from a child under the
                                applicable age without parental consent, we will take steps to delete that information
                                as soon as possible.
                            </p>
                        </section>

                        <section>
                            <h2>8.1 International Data Transfers</h2>
                            <p>
                                Your personal data may be transferred to and processed in countries other than your country
                                of residence. We use AWS services that may process data in various regions. We ensure
                                that appropriate safeguards are in place, including:
                            </p>
                            <ul>
                                <li>Standard Contractual Clauses (SCCs) approved by the European Commission</li>
                                <li>AWS Data Processing Addendum and compliance with GDPR</li>
                                <li>Other legal mechanisms to ensure adequate protection of your data</li>
                            </ul>
                            <p>
                                By using our service, you consent to the transfer of your data to these locations.
                            </p>
                        </section>

                        <section>
                            <h2>9. Cookies and Tracking Technologies</h2>
                            <p>
                                We use cookies and similar tracking technologies to enhance your experience, analyze usage,
                                and assist with security. Types of cookies we use include:
                            </p>
                            <ul>
                                <li>
                                    <strong>Essential Cookies:</strong> Required for the service to function (authentication,
                                    security, session management)
                                </li>
                                <li>
                                    <strong>Analytics Cookies:</strong> Help us understand how users interact with our
                                    service (used with your consent where required)
                                </li>
                                <li>
                                    <strong>Preference Cookies:</strong> Remember your settings and preferences
                                </li>
                            </ul>
                            <p>
                                You can control cookies through your browser settings. However, disabling essential cookies
                                may affect service functionality.
                            </p>
                        </section>

                        <section>
                            <h2>10. Changes to This Privacy Policy</h2>
                            <p>
                                We may update this Privacy Policy from time to time to reflect changes in our practices,
                                technology, legal requirements, or other factors. We will notify you of material changes by:
                            </p>
                            <ul>
                                <li>Posting the updated Privacy Policy on this page</li>
                                <li>Updating the "Last updated" date</li>
                                <li>Sending an email notification for significant changes (where required by law)</li>
                                <li>Displaying a prominent notice on our website</li>
                            </ul>
                            <p>
                                Your continued use of the service after changes become effective constitutes acceptance of
                                the updated policy. We encourage you to review this policy periodically.
                            </p>
                        </section>

                        <section>
                            <h2>11. Contact Us and Data Protection Officer</h2>
                            <p>
                                If you have any questions, concerns, or requests regarding this Privacy Policy or our data
                                practices, please contact us:
                            </p>
                            <p>
                                <strong>Email:</strong> <a href='mailto:hydromastery@gmail.com'>hydromastery@gmail.com</a>
                                <br />
                                <strong>Subject Line:</strong> "Privacy Policy Inquiry" or "Data Protection Request"
                            </p>
                            <p>
                                For users in the European Economic Area (EEA), you also have the right to contact your
                                local data protection authority if you have concerns about how we handle your personal data.
                            </p>
                            <p>
                                We are committed to responding to your inquiries promptly and addressing any concerns you
                                may have about your privacy and data protection.
                            </p>
                        </section>
                    </div>
                </div>
            </section>
        </div>
    );
}

